import { isNil } from 'lodash';
import React, { CSSProperties, useContext } from 'react';
import { GestureResponderEvent, TouchableOpacity } from 'react-native';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBSeparator } from '@shared/components/list';
import {
  MBTooltip,
  MBTooltipHorizontalArrowAlignment,
  MBTooltipVerticalArrowAlignment,
} from '@shared/components/tooltip';
import { MBTextBody1, MBTextBody1Bold } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { MBOverlay } from '@shared/util/overlay';
import { MBActionSheetOption, MBActionSheetShowOptions } from './types';

///////////////////////////////// INTERFACES /////////////////////////////////
interface MBActionSheetOptionListRowProps {
  option: MBActionSheetOption;
  onPress(ev: GestureResponderEvent): void;
}

interface MBActionSheetTooltipProps {
  title?: string;
  options: MBActionSheetOption[];
  parent?: React.Component;
  close: () => void;
  verticalArrowAlignment: MBTooltipVerticalArrowAlignment;
  horizontalArrowAlignment: MBTooltipHorizontalArrowAlignment;
}

///////////////////////////////// COMPONENTS /////////////////////////////////
/*
    A list item for MBActionSheet options
*/
function MBActionSheetOptionListRow({ option, onPress }: MBActionSheetOptionListRowProps) {
  const colors = useContext(ColorThemeContext);
  return (
    <TouchableOpacity onPress={onPress}>
      <MBHover hoverStyle={{ backgroundColor: colors.backgroundInactive }}>
        <View paddingHorizontal={2} paddingVertical={1}>
          <MBTextBody1
            color={option.destructive ? colors.error.foreground : colors.foregroundActive}
          >
            {option.title}
          </MBTextBody1>
        </View>
      </MBHover>
    </TouchableOpacity>
  );
}

function MBActionSheetTooltip({
  parent,
  title,
  options,
  close,
  ...tooltipProps
}: MBActionSheetTooltipProps) {
  const colors = useContext(ColorThemeContext);
  const filteredOptions = options.filter(option => !option.hidden);
  if (!filteredOptions) {
    return null;
  }
  return (
    <MBTooltip padding={0} {...tooltipProps}>
      {!!title && (
        <View>
          <View paddingHorizontal={2} paddingVertical={1}>
            <MBTextBody1Bold color={colors.foregroundActive}>{title}</MBTextBody1Bold>
          </View>
        </View>
      )}
      {!!title && !!filteredOptions.length && <MBSeparator />}
      {filteredOptions.map((option, index) => (
        <MBActionSheetOptionListRow
          key={index}
          option={option}
          onPress={ev => {
            close();
            option.onPress?.(parent);
            // If not calling preventDefault, the DOM element
            // under the clicked option will also be clicked.
            ev.preventDefault();
          }}
        />
      ))}
    </MBTooltip>
  );
}

///////////////////////////////// HELPER FUNCTIONS /////////////////////////////////
/*
    Positions the tooltip relative tot the parent rect depending
    on tooltip arrow alignment.
*/
function getPosition(
  parentRect: DOMRect,
  verticalArrowAlignment: MBTooltipVerticalArrowAlignment,
  horizontalArrowAlignment: MBTooltipHorizontalArrowAlignment,
): CSSProperties {
  const position = 'absolute';
  const maxWidth = 250;
  let top: number | undefined;
  let bottom: number | undefined;
  let left: number | undefined;
  let right: number | undefined;
  if (verticalArrowAlignment === 'above') {
    bottom = window.innerHeight - parentRect.top;
  } else {
    top = parentRect.top + parentRect.height;
  }

  if (horizontalArrowAlignment === 'left') {
    left = parentRect.left;
  } else if (horizontalArrowAlignment === 'right') {
    right = window.innerWidth - parentRect.right;
  } else {
    // TODO(fant): same positioning as left
    left = parentRect.left;
  }

  return {
    position,
    maxWidth: getPx(maxWidth),
    top: getPx(top),
    bottom: getPx(bottom),
    left: getPx(left),
    right: getPx(right),
  };
}

export function getPx(value: number | undefined): string | undefined {
  if (isNil(value)) {
    return undefined;
  } else {
    return `${value}px`;
  }
}

///////////////////////////////// IMPLEMENTATION /////////////////////////////////
/*
    This is the equivalent of the native action sheet. The current way of doing
    it is somewhat hacky, but it creates a simple interface to use
    MBActionSheet.

    What we do is attaching our custom React component to a new virtual React
    DOM in another HTML element (#shared-components-tooltip). This is then
    absolutely positioned below the parent element to make it look like they're
    connected. react-modal works in a similar way (which MBModal is built on.)
    This is how it's possible to overlay all other content and for child views
    to appear somewhere unrelated to the parent's position.

    Caveats: if the content/window sizing changes, the action sheet options
    won't change with it.
*/
export const MBActionSheet = {
  show: ({
    title,
    parent,
    options,
    webHorizontalAlignment = 'left',
    webVerticalAlignment = 'below',
    webPosition = parentRect =>
      getPosition(parentRect, webVerticalAlignment, webHorizontalAlignment),
    onCancel,
    onClose,
  }: MBActionSheetShowOptions) => {
    if (isNil(parent)) {
      return;
    }
    MBOverlay.show({
      parent,
      position: webPosition,
      closeOnBackdropPress: true,
      onBackdropPress: () => {
        onCancel?.();
        onClose?.();
      },
      // TODO(fant): figure out how to make this HOC situation nicer
      component: ({ close }) => (
        <MBActionSheetTooltip
          parent={parent}
          title={title}
          options={options}
          close={() => {
            close();
            onClose?.();
          }}
          verticalArrowAlignment={webVerticalAlignment}
          horizontalArrowAlignment={webHorizontalAlignment}
        />
      ),
    });
  },
};
